import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import MentionText from "../../Shared/MentionText";
import httpService from "../../../services/http.service";
import {ShimmerRows} from "../../Shared/Shimmer";
import {decideReport, formatReportDate, MediaGallery, ReasonBadges, StateBadge, TypeBadge, UserCell} from "./reportUi";

/**
 * Fiche d'un signalement en panneau latéral (l'URL ne change pas : pas d'identifiant dans les URL).
 * Contenu visé, auteur, signalement choisi, historique de tous les signalements du même contenu, décision.
 */
const ReportDrawer = ({report, onClose, onDecided}) => {
    const {t} = useTranslation();
    const [detail, setDetail] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let alive = true;
        setDetail(null);
        setError(null);
        (async () => {
            try {
                const {data} = await httpService.get(`/reports/${report.report_id}`);
                if (alive) setDetail(data?.data);
            } catch (e) {
                if (alive) setError(e?.response?.data?.message || t("Erreur de chargement."));
            }
        })();
        const onKey = (e) => e.key === 'Escape' && onClose();
        window.addEventListener('keydown', onKey);
        return () => { alive = false; window.removeEventListener('keydown', onKey); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [report.report_id]);

    const current = detail ? {...detail, report_state: report.report_state || detail.report_state} : null;

    const decide = async (target, decision) => {
        setBusy(true);
        const updated = await decideReport(target, decision);
        setBusy(false);
        if (!updated) return;
        onDecided(updated);
        setDetail(d => d && ({
            ...d,
            ...(d.report_id === updated.report_id ? {report_state: updated.report_state} : {}),
            history: d.history.map(h => h.report_id === updated.report_id ? {...h, report_state: updated.report_state} : h),
        }));
    };

    return (
        <>
            <div onClick={onClose} style={{position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 1040}} />
            <aside role="dialog" aria-label={t("Fiche du signalement")}
                   style={{position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(560px, 100vw)', background: 'var(--app-surface, #fff)', zIndex: 1050, overflowY: 'auto', boxShadow: '-4px 0 24px rgba(0,0,0,.15)'}}>
                <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
                    <h5 className="mb-0">{t("Fiche du signalement")}</h5>
                    <button type="button" className="close" onClick={onClose} aria-label={t("Fermer")}><span aria-hidden="true">×</span></button>
                </div>

                <div className="p-3">
                    {error && <div className="alert alert-danger">{error}</div>}
                    {!current && !error && <ShimmerRows rows={8} />}

                    {current && (
                        <>
                            <div className="mb-3 d-flex align-items-center">
                                <TypeBadge type={current.type} />
                                <span className="ml-2"><StateBadge state={current.report_state} /></span>
                                <span className="ml-auto badge badge-danger">{t("{{n}} signalement(s) sur ce contenu", {n: current.same_content_count})}</span>
                            </div>

                            <h6 className="text-muted text-uppercase small">{t("Contenu signalé")}</h6>
                            <div className="card mb-3">
                                <div className="body">
                                    {current.content ? (
                                        <>
                                            <p style={{whiteSpace: 'pre-wrap'}} className="mb-2">{(current.content.text && <MentionText text={current.content.text} />) || <em className="text-muted">{current.type === 'user' ? t("Compte signalé depuis une conversation privée.") : t("(sans texte)")}</em>}</p>
                                            <MediaGallery media={(current.content.media || []).filter(m => m.source !== 'attachment')} />
                                            {(current.content.media || []).some(m => m.source === 'attachment') && (
                                                <>
                                                    <div className="text-muted small text-uppercase mt-3">{t("Pièces jointes")}</div>
                                                    <MediaGallery media={current.content.media.filter(m => m.source === 'attachment')} />
                                                </>
                                            )}
                                            <div className="d-flex justify-content-between align-items-end small">
                                                <div><span className="text-muted">{current.type === 'user' ? t("Compte") : t("Auteur")} : </span><UserCell user={current.content.author} /></div>
                                                <span className="text-muted">{formatReportDate(current.content.date)}</span>
                                            </div>
                                        </>
                                    ) : <em className="text-muted">{t("Contenu supprimé depuis le signalement.")}</em>}
                                </div>
                            </div>

                            <h6 className="text-muted text-uppercase small">{t("Signalement")}</h6>
                            <table className="table table-sm mb-3">
                                <tbody>
                                <tr><th className="w-25">{t("Signalé par")}</th><td><UserCell user={current.reporter} /></td></tr>
                                <tr><th>{t("Date")}</th><td>{formatReportDate(current.report_date)}</td></tr>
                                <tr><th>{t("Motifs")}</th><td><ReasonBadges reasons={current.reasons} /></td></tr>
                                <tr><th>{t("Description")}</th><td style={{whiteSpace: 'pre-wrap'}}>{current.descr || <span className="text-muted">–</span>}</td></tr>
                                </tbody>
                            </table>

                            <div className="d-flex mb-4">
                                <button type="button" className="btn btn-success flex-fill mr-2" disabled={busy || current.report_state === 'approved'} onClick={() => decide(current, 'approve')}>
                                    <i className="fa fa-check"></i>{' '}{t("Valider")}
                                </button>
                                <button type="button" className="btn btn-outline-danger flex-fill" disabled={busy || current.report_state === 'rejected'} onClick={() => decide(current, 'reject')}>
                                    <i className="fa fa-times"></i>{' '}{t("Rejeter")}
                                </button>
                            </div>

                            <h6 className="text-muted text-uppercase small">{t("Historique des signalements de ce contenu")}</h6>
                            <ul className="list-group">
                                {current.history.map(item => (
                                    <li key={item.report_id} className={`list-group-item ${item.report_id === current.report_id ? 'list-group-item-light' : ''}`}>
                                        <div className="d-flex justify-content-between align-items-start">
                                            <UserCell user={item.reporter} />
                                            <div className="text-right">
                                                <StateBadge state={item.report_state} />
                                                <div className="text-muted small">{formatReportDate(item.report_date)}</div>
                                            </div>
                                        </div>
                                        <div className="mt-1"><ReasonBadges reasons={item.reasons} /></div>
                                        {item.descr && <div className="small text-muted mt-1" style={{whiteSpace: 'pre-wrap'}}>{item.descr}</div>}
                                        {item.report_id !== current.report_id && item.report_state === 'pending' && (
                                            <div className="mt-2">
                                                <button type="button" className="btn btn-sm btn-link text-success p-0 mr-3" disabled={busy} onClick={() => decide(item, 'approve')}>{t("Valider")}</button>
                                                <button type="button" className="btn btn-sm btn-link text-danger p-0" disabled={busy} onClick={() => decide(item, 'reject')}>{t("Rejeter")}</button>
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </div>
            </aside>
        </>
    );
};

export default ReportDrawer;
